/**
 * Growtype Analytics — lightweight real-user performance monitoring.
 *
 * Captures one compact sampled `web_performance` snapshot per page without
 * storing request payloads, full user-agent strings, or resource query strings.
 */
;(function () {
    'use strict';

    function initializeRum() {

    if (window.__growtypeAnalyticsRumLoaded) return;
    window.__growtypeAnalyticsRumLoaded = true;

    var config = window.gaRumConfig || {};
    var navigation = window.performance && performance.getEntriesByType
        ? performance.getEntriesByType('navigation')[0]
        : null;
    var metrics = {
        fcp: null,
        lcp: null,
        cls: 0,
        inp: null,
    };
    var lcpDetails = null;
    var inpDetails = null;
    var interactions = {};
    var clsSessionValue = 0;
    var clsSessionStart = 0;
    var clsSessionLast = 0;
    var lastPayloadFingerprint = '';
    var reportCount = 0;
    var maxReportsPerPage = 1;
    var reportSampleRate = Number(config.reportSampleRate);
    if (!isFinite(reportSampleRate)) reportSampleRate = 0.05;
    reportSampleRate = Math.max(0, Math.min(1, reportSampleRate));
    var reportSampled = Math.random() < reportSampleRate;
    var maxEventTimingMs = 60000;
    var cacheProbe = {
        status: 'not_probed',
        age_seconds: null,
        litespeed_status: '',
    };

    function round(value, precision) {
        if (typeof value !== 'number' || !isFinite(value)) return null;
        var multiplier = Math.pow(10, precision || 0);
        return Math.round(value * multiplier) / multiplier;
    }

    function validEventTiming(value) {
        if (typeof value !== 'number' || !isFinite(value) || value < 0 || value > maxEventTimingMs) {
            return null;
        }

        return round(value, 1);
    }

    function cleanResourceUrl(value) {
        try {
            var url = new URL(value, window.location.href);
            return url.origin + url.pathname;
        } catch (error) {
            return String(value || '').split('?')[0].substring(0, 500);
        }
    }

    function elementDescription(element) {
        if (!element || !element.tagName) return '';
        var description = element.tagName.toLowerCase();
        if (element.id) description += '#' + String(element.id).substring(0, 80);
        if (element.classList && element.classList.length) {
            description += '.' + Array.prototype.slice.call(element.classList, 0, 3).join('.');
        }
        return description.substring(0, 180);
    }

    function observe(type, callback, options) {
        if (!window.PerformanceObserver) return;
        try {
            var observer = new PerformanceObserver(callback);
            observer.observe(options || {type: type, buffered: true});
        } catch (error) {
            // Unsupported entry type. RUM must never affect the page.
        }
    }

    function classifyNavigation() {
        var navType = navigation && navigation.type ? navigation.type : 'navigate';
        var currentUrl = window.location.pathname + window.location.search;
        var internalMarker = null;
        var firstVisit = true;

        try {
            firstVisit = sessionStorage.getItem('ga_rum_seen') !== '1';
            internalMarker = JSON.parse(sessionStorage.getItem('ga_rum_internal_navigation') || 'null');
            sessionStorage.setItem('ga_rum_seen', '1');
            sessionStorage.removeItem('ga_rum_internal_navigation');
        } catch (error) {}

        if (navType === 'reload') return 'reload';
        if (navType === 'back_forward') return 'back_forward';
        if (navigation && navigation.transferSize === 0 && navigation.decodedBodySize > 0) return 'browser_cache';
        if (
            internalMarker &&
            internalMarker.url === currentUrl &&
            Date.now() - internalMarker.timestamp < 30000
        ) {
            return 'internal_navigation';
        }
        return firstVisit ? 'first_visit' : 'direct_navigation';
    }

    var experienceType = classifyNavigation();

    document.addEventListener('click', function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
        if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (link.target && link.target !== '_self') return;

        try {
            var destination = new URL(link.href, window.location.href);
            if (destination.origin !== window.location.origin) return;
            sessionStorage.setItem('ga_rum_internal_navigation', JSON.stringify({
                url: destination.pathname + destination.search,
                timestamp: Date.now(),
            }));
        } catch (error) {}
    }, true);

    observe('paint', function (list) {
        list.getEntries().forEach(function (entry) {
            if (entry.name === 'first-contentful-paint') metrics.fcp = round(entry.startTime, 1);
        });
    }, {type: 'paint', buffered: true});

    observe('largest-contentful-paint', function (list) {
        var entries = list.getEntries();
        var entry = entries[entries.length - 1];
        if (!entry) return;
        metrics.lcp = round(entry.startTime, 1);
        lcpDetails = {
            element: elementDescription(entry.element),
            url: cleanResourceUrl(entry.url || ''),
            size: entry.size || 0,
        };
    }, {type: 'largest-contentful-paint', buffered: true});

    observe('layout-shift', function (list) {
        list.getEntries().forEach(function (entry) {
            if (entry.hadRecentInput) return;

            if (
                clsSessionValue > 0 &&
                entry.startTime - clsSessionLast < 1000 &&
                entry.startTime - clsSessionStart < 5000
            ) {
                clsSessionValue += entry.value;
            } else {
                clsSessionValue = entry.value;
                clsSessionStart = entry.startTime;
            }

            clsSessionLast = entry.startTime;
            metrics.cls = Math.max(metrics.cls, clsSessionValue);
        });
        metrics.cls = round(metrics.cls, 4);
    }, {type: 'layout-shift', buffered: true});

    function refreshInp() {
        var durations = Object.keys(interactions).map(function (key) {
            return interactions[key];
        }).sort(function (a, b) {
            return b.duration - a.duration;
        });
        if (!durations.length) return;

        // INP ignores one worst interaction for every 50 interactions.
        var index = Math.min(Math.floor(durations.length / 50), durations.length - 1);
        metrics.inp = round(durations[index].duration, 1);
        inpDetails = durations[index].details;
    }

    observe('event', function (list) {
        list.getEntries().forEach(function (entry) {
            if (!entry.interactionId) return;
            var key = String(entry.interactionId);
            var duration = validEventTiming(entry.duration);
            if (duration === null) return;
            if (!interactions[key] || duration > interactions[key].duration) {
                interactions[key] = {
                    duration: duration,
                    details: {
                        name: String(entry.name || '').substring(0, 50),
                        target: elementDescription(entry.target),
                    },
                };
            }
        });
        refreshInp();
    }, {type: 'event', buffered: true, durationThreshold: 16});

    observe('first-input', function (list) {
        var entry = list.getEntries()[0];
        if (!entry || metrics.inp !== null) return;
        var duration = validEventTiming(entry.processingStart - entry.startTime);
        if (duration === null) return;
        metrics.inp = duration;
        inpDetails = {
            name: String(entry.name || '').substring(0, 50),
            target: elementDescription(entry.target),
            fallback: 'first_input_delay',
        };
    }, {type: 'first-input', buffered: true});

    function getServerTiming(entry) {
        var result = {};
        if (!entry || !entry.serverTiming) return result;
        Array.prototype.forEach.call(entry.serverTiming, function (item) {
            var name = String(item.name || '').toLowerCase();
            if (name.indexOf('cache') !== -1 || name.indexOf('cdn') !== -1 || name.indexOf('cf') === 0) {
                result[name.substring(0, 50)] = String(item.description || item.duration || '').substring(0, 100);
            }
        });
        return result;
    }

    function resourceCategory(entry) {
        var type = String(entry.initiatorType || 'other').toLowerCase();
        var path = String(entry.name || '');
        if (type === 'img' || /\.(avif|gif|jpe?g|png|svg|webp)(\?|$)/i.test(path)) return 'image';
        if (type === 'script' || /\.js(\?|$)/i.test(path)) return 'script';
        if (type === 'fetch' || type === 'xmlhttprequest' || /admin-ajax\.php|\/wp-json\//i.test(path)) return 'ajax';
        return type.substring(0, 30);
    }

    function slowResources() {
        if (!window.performance || !performance.getEntriesByType) return [];

        var documentUrl = cleanResourceUrl(window.location.href);

        var resources = performance.getEntriesByType('resource')
            .filter(function (entry) {
                var cleanUrl = cleanResourceUrl(entry.name);
                if (cleanUrl.indexOf('/growtype-analytics/v1/track') !== -1) return false;
                // Exclude the delayed document HEAD cache probe from the slowest
                // resource list; it is reported separately under cdn_cache.
                return !(cleanUrl === documentUrl && entry.initiatorType === 'fetch');
            })
            .map(function (entry) {
                var browserCache = entry.transferSize === 0 && entry.decodedBodySize > 0;
                return {
                    url: cleanResourceUrl(entry.name),
                    category: resourceCategory(entry),
                    duration_ms: round(entry.duration, 1),
                    ttfb_ms: round(entry.responseStart > 0 ? entry.responseStart - entry.startTime : 0, 1),
                    download_ms: round(entry.responseEnd > 0 && entry.responseStart > 0 ? entry.responseEnd - entry.responseStart : 0, 1),
                    transfer_bytes: entry.transferSize || 0,
                    encoded_bytes: entry.encodedBodySize || 0,
                    protocol: String(entry.nextHopProtocol || '').substring(0, 30),
                    cache_status: browserCache ? 'browser_hit' : 'network',
                    server_timing: getServerTiming(entry),
                };
            })
            .sort(function (a, b) {
                return b.duration_ms - a.duration_ms;
            });

        var categories = ['image', 'script', 'ajax'];
        var selected = [];
        categories.forEach(function (category) {
            selected = selected.concat(resources.filter(function (resource) {
                return resource.category === category;
            }).slice(0, 3));
        });

        return selected.sort(function (a, b) {
            return b.duration_ms - a.duration_ms;
        });
    }

    function connectionDetails() {
        var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
        return {
            effective_type: String(connection.effectiveType || 'unknown').substring(0, 20),
            downlink_mbps: round(connection.downlink, 2),
            rtt_ms: round(connection.rtt, 0),
            save_data: !!connection.saveData,
        };
    }

    function deviceDetails() {
        return {
            class: window.matchMedia && window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'desktop',
            mobile_hint: !!(navigator.userAgentData && navigator.userAgentData.mobile),
            viewport_width: window.innerWidth || 0,
            viewport_height: window.innerHeight || 0,
            screen_width: window.screen ? window.screen.width : 0,
            screen_height: window.screen ? window.screen.height : 0,
            pixel_ratio: round(window.devicePixelRatio || 1, 2),
            memory_gb: navigator.deviceMemory || null,
            cpu_cores: navigator.hardwareConcurrency || null,
        };
    }

    function shouldProbeDocumentCache() {
        if (!reportSampled || !config.cacheProbe || !window.fetch || document.visibilityState === 'hidden') return false;

        var sampleRate = Math.max(0, Math.min(1, Number(config.cacheProbeSampleRate) || 0));
        if (sampleRate <= 0) return false;

        try {
            var sampleKey = 'ga_rum_cache_probe_sample';
            var sampled = sessionStorage.getItem(sampleKey);
            if (sampled === null) {
                sampled = Math.random() < sampleRate ? '1' : '0';
                sessionStorage.setItem(sampleKey, sampled);
            }
            if (sampled !== '1') {
                cacheProbe.status = 'not_sampled';
                return false;
            }

            var urlsKey = 'ga_rum_cache_probe_urls';
            var visited = JSON.parse(sessionStorage.getItem(urlsKey) || '[]');
            var path = window.location.pathname;
            if (visited.indexOf(path) !== -1) {
                cacheProbe.status = 'already_probed_this_session';
                return false;
            }
            visited.push(path);
            sessionStorage.setItem(urlsKey, JSON.stringify(visited.slice(-50)));
        } catch (error) {
            // If storage is unavailable, retain sampling without per-URL state.
            if (Math.random() >= sampleRate) {
                cacheProbe.status = 'not_sampled';
                return false;
            }
        }

        return true;
    }

    function probeDocumentCache() {
        if (!shouldProbeDocumentCache()) return Promise.resolve();
        cacheProbe.status = 'probe_pending';
        return fetch(window.location.href, {
            method: 'HEAD',
            credentials: 'same-origin',
            cache: 'default',
        }).then(function (response) {
            cacheProbe.status = response.headers.get('cf-cache-status') || 'header_unavailable';
            var age = parseInt(response.headers.get('age') || '', 10);
            cacheProbe.age_seconds = isNaN(age) ? null : age;
            cacheProbe.litespeed_status = response.headers.get('x-litespeed-cache') || '';
        }).catch(function () {
            cacheProbe.status = 'probe_failed';
        });
    }

    function buildPayload(reason) {
        var responseStart = navigation ? navigation.responseStart : 0;
        var requestStart = navigation ? navigation.requestStart : 0;
        return {
            reason: reason,
            vitals: {
                ttfb_ms: round(responseStart > 0 ? responseStart - requestStart : null, 1),
                fcp_ms: metrics.fcp,
                lcp_ms: metrics.lcp,
                inp_ms: metrics.inp,
                cls: round(metrics.cls, 4),
            },
            lcp_element: lcpDetails,
            inp_interaction: inpDetails,
            navigation: {
                browser_type: navigation && navigation.type ? navigation.type : 'unknown',
                experience_type: experienceType,
                dom_content_loaded_ms: navigation ? round(navigation.domContentLoadedEventEnd, 1) : null,
                load_ms: navigation ? round(navigation.loadEventEnd, 1) : null,
                transfer_bytes: navigation ? navigation.transferSize || 0 : 0,
                encoded_bytes: navigation ? navigation.encodedBodySize || 0 : 0,
                protocol: navigation ? String(navigation.nextHopProtocol || '').substring(0, 30) : '',
                server_timing: getServerTiming(navigation),
            },
            cdn_cache: cacheProbe,
            connection: connectionDetails(),
            device: deviceDetails(),
            slow_resources: slowResources(),
        };
    }

    function report(reason) {
        if (!reportSampled || typeof window.gaTrack !== 'function' || reportCount >= maxReportsPerPage) return;
        refreshInp();
        var payload = buildPayload(reason);
        var fingerprintPayload = Object.assign({}, payload, {reason: ''});
        var fingerprint = JSON.stringify(fingerprintPayload);
        if (fingerprint === lastPayloadFingerprint) return;
        lastPayloadFingerprint = fingerprint;
        reportCount += 1;
        window.gaTrack('web_performance', {
            object_id: window.location.pathname,
            object_type: 'page',
            metadata: payload,
        });
    }

    function scheduleAfterLoad() {
        var start = function () {
            var run = function () {
                probeDocumentCache().then(function () {
                    report('settled');
                });
            };
            if (window.requestIdleCallback) {
                requestIdleCallback(run, {timeout: 3000});
            } else {
                setTimeout(run, 2000);
            }
        };

        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, {once: true});
    }

    try {
        if (window.performance && performance.setResourceTimingBufferSize) {
            performance.setResourceTimingBufferSize(500);
        }
    } catch (error) {}

    scheduleAfterLoad();
    }

    if (document.prerendering) {
        document.addEventListener('prerenderingchange', initializeRum, {once: true});
    } else {
        initializeRum();
    }
})();
